import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, serial, text, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { numericAsNumber } from '../../custom-type'
import { organization } from '../auth'
import { File } from '../file'
import { InvoiceStatusEnum, MembershipTypeEnum } from './enums'
import { Price } from './price'
import { Voucher } from './voucher/voucher'

export const InvoiceSequence = pgTable(
  'invoice_sequence',
  {
    id: serial('id').primaryKey(),
    year: integer('year').notNull(),
    month: integer('month').notNull(),
    sequence: integer('sequence').notNull(),
  },
  (table) => [uniqueIndex('year_month_unique_idx').on(table.year, table.month)],
)

export const Invoice = pgTable(
  'invoice',
  {
    id: uuid().primaryKey().defaultRandom(),
    invoiceNumber: text().notNull(),
    xenditId: text(),
    organizationId: text().references(() => organization.id, { onDelete: 'set null' }),
    priceId: uuid().references(() => Price.id, { onDelete: 'set null' }),
    voucherId: uuid().references(() => Voucher.id, { onDelete: 'set null' }),
    totalPrice: numericAsNumber().notNull(),
    totalDiscount: numericAsNumber(),
    amountPaid: numericAsNumber().notNull(),
    status: InvoiceStatusEnum().notNull().default('PENDING'),
    type: MembershipTypeEnum().notNull().default('NEW'),
    seats: integer().notNull(),
    paidDate: timestamp({ mode: 'date', withTimezone: true }),
    customMembershipStartDate: timestamp({ mode: 'date', withTimezone: true }),
    expiredAt: timestamp({ mode: 'date', withTimezone: true }),
    triggerId: text(),
    triggerPublicToken: text(),
    paymentUrl: text(),
    perSeatPrice: numericAsNumber().notNull(),
    paymentMethod: text(),
    xenditMetadata: text(),
    membershipStartDate: timestamp({ mode: 'date', withTimezone: true }),
    membershipEndDate: timestamp({ mode: 'date', withTimezone: true }),
    // Offline-paid (DIRECT_PAID) support: uploaded receipt + the offline invoice/PO reference.
    receiptFileId: uuid().references(() => File.id, { onDelete: 'set null' }),
    externalReference: text(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    // Index for organization lookups
    index('idx_invoice_organization').on(t.organizationId),
  ],
)

export const invoiceRelations = relations(Invoice, ({ one }) => ({
  organization: one(organization, {
    fields: [Invoice.organizationId],
    references: [organization.id],
  }),

  price: one(Price, {
    fields: [Invoice.priceId],
    references: [Price.id],
  }),

  voucher: one(Voucher, {
    fields: [Invoice.voucherId],
    references: [Voucher.id],
  }),

  receiptFile: one(File, {
    fields: [Invoice.receiptFileId],
    references: [File.id],
  }),
}))

export const InsertInvoiceSchema = createInsertSchema(Invoice, {
  invoiceNumber: z.string().min(1),
  xenditId: z.string().optional(),
  organizationId: z.string(),
  priceId: z.uuid().optional(),
  voucherId: z.uuid().optional(),
  totalPrice: z.number().nonnegative(),
  totalDiscount: z.number().nonnegative().optional(),
  amountPaid: z.number().nonnegative(),
  status: z.enum(InvoiceStatusEnum.enumValues).default('PENDING'),
  type: z.enum(MembershipTypeEnum.enumValues).default('NEW'),
  seats: z.number().int().positive(),
  paidDate: z.date().optional(),
  customMembershipStartDate: z.date().optional(),
  expiredAt: z.date().optional(),
  triggerId: z.string().optional(),
  triggerPublicToken: z.string().optional(),
  perSeatPrice: z.number().nonnegative(),
  paymentMethod: z.string().optional(),
  xenditMetadata: z.record(z.string(), z.any()).optional(),
  paymentUrl: z.url().optional(),
  receiptFileId: z.uuid().optional(),
  externalReference: z.string().optional(),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

export const UpdateInvoiceSchema = createUpdateSchema(Invoice, {
  xenditId: z.string(),
  status: z.enum(InvoiceStatusEnum.enumValues),
  paidDate: z.date(),
  expiredAt: z.date(),
  triggerId: z.string(),
  triggerPublicToken: z.string(),
  perSeatPrice: z.number().nonnegative(),
  paymentMethod: z.string().nullable(),
  xenditMetadata: z.record(z.string(), z.any()).nullable(),
  paymentUrl: z.url().nullable(),
})
  .omit({
    id: true,
    invoiceNumber: true,
    totalPrice: true,
    totalDiscount: true,
    customMembershipStartDate: true,
    amountPaid: true,
    seats: true,
    type: true,
    organizationId: true,
    priceId: true,
    voucherId: true,
    paymentUrl: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial()

export const SelectInvoiceSchema = createSelectSchema(Invoice, {
  id: z.uuid(),
  invoiceNumber: z.string(),
  xenditId: z.string().nullable(),
  organizationId: z.string().nullable(),
  priceId: z.uuid().nullable(),
  voucherId: z.uuid().nullable(),
  totalPrice: z.number().int(),
  totalDiscount: z.number().int().nullable(),
  amountPaid: z.number().int(),
  status: z.enum(InvoiceStatusEnum.enumValues),
  type: z.enum(MembershipTypeEnum.enumValues),
  seats: z.number().int(),
  paidDate: z.date().nullable(),
  customMembershipStartDate: z.date().nullable(),
  expiredAt: z.date().nullable(),
  triggerId: z.string().nullable(),
  triggerPublicToken: z.string().nullable(),
  perSeatPrice: z.number().nonnegative(),
  paymentMethod: z.string().nullable(),
  xenditMetadata: z.record(z.string(), z.any()).nullable(),
  paymentUrl: z.url().nullable(),
  receiptFileId: z.uuid().nullable(),
  externalReference: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
})

export type InsertInvoice = typeof Invoice.$inferInsert
export type UpdateInvoice = z.infer<typeof UpdateInvoiceSchema>
export type SelectInvoice = typeof Invoice.$inferSelect
